import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BadgeCheck, ArrowRight } from "lucide-react";
import { marketplaceApi } from "@/lib/api";

const SWATCHES = [
  "bg-gradient-to-br from-navy to-navy-light",
  "bg-gradient-to-br from-gold to-amber-600",
  "bg-gradient-to-br from-emerald-500 to-teal-700",
  "bg-gradient-to-br from-indigo-500 to-purple-700",
  "bg-gradient-to-br from-rose-500 to-red-700",
  "bg-gradient-to-br from-cyan-500 to-blue-700",
];

interface Seller {
  id: number;
  name: string;
  slug: string | null;
  logoUrl: string | null;
  coverUrl: string | null;
  isVerified: boolean;
  products: number;
  services: number;
  categories: Set<string>;
}

const TopSellers = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [products, services] = await Promise.all([
          marketplaceApi.listProducts(),
          marketplaceApi.listServices(),
        ]);
        const map = new Map<number, Seller>();
        const add = (
          owner: number,
          display: string,
          slug: string | null,
          logo: string | null,
          cover: string | null,
          verified: boolean,
          category: string | null,
          kind: "products" | "services",
        ) => {
          const entry = map.get(owner) || {
            id: owner, name: display, slug,
            logoUrl: logo, coverUrl: cover, isVerified: verified,
            products: 0, services: 0, categories: new Set<string>(),
          };
          entry[kind] += 1;
          if (!entry.logoUrl && logo) entry.logoUrl = logo;
          if (!entry.coverUrl && cover) entry.coverUrl = cover;
          if (verified) entry.isVerified = true;
          if (category) entry.categories.add(category);
          map.set(owner, entry);
        };
        products.forEach(p => add(
          p.owner, p.owner_company_name || p.owner_name, p.owner_slug,
          p.owner_logo_url, p.owner_cover_url, p.owner_is_verified, p.category_name, "products",
        ));
        services.forEach(s => add(
          s.owner, s.owner_company_name || s.owner_name, s.owner_slug,
          s.owner_logo_url, s.owner_cover_url, s.owner_is_verified, s.category_name, "services",
        ));
        const top = Array.from(map.values())
          .sort((a, b) => (b.products + b.services) - (a.products + a.services))
          .slice(0, 10);
        if (alive) setSellers(top);
      } catch {
        /* ignore — section stays empty */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loading && sellers.length === 0) return null;

  return (
    <section className="relative py-14 bg-gradient-to-br from-navy via-navy to-navy-light overflow-hidden">
      {/* Decorative glow accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-gold/5 blur-3xl" />

      <div className="relative container mx-auto px-4">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white leading-snug">
              Top sellers
            </h2>
            <p className="text-sm text-white/60 mt-2 max-w-lg">
              The most active suppliers and providers trading across the AAMECC network.
            </p>
          </div>
          <Link
            to="/marketplace/products"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline shrink-0"
          >
            Browse marketplace <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-pulse p-3">
                <div className="aspect-[16/9] w-full rounded-lg bg-gray-100 mb-2.5" />
                <div className="px-1">
                  <div className="h-3.5 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {sellers.map((seller, i) => {
              const key = seller.slug || seller.id;
              const swatch = SWATCHES[seller.id % SWATCHES.length];
              const category = Array.from(seller.categories)[0];
              return (
                <motion.div
                  key={seller.id}
                  initial={{ opacity: 0, y: 32, scale: 0.94 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: Math.min(i * 0.06, 0.5), ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-gold/40 transition-[box-shadow,border-color]"
                >
                  <Link to={`/marketplace/vendor/${key}`} className="block p-3">
                    {/* Large logo panel — no cover crop; the full mark always shows */}
                    <div className="aspect-[16/9] w-full rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center mb-2.5">
                      {seller.logoUrl ? (
                        <img src={seller.logoUrl} alt={seller.name} className="w-full h-full object-contain p-3" loading="lazy" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center text-3xl font-extrabold text-white ${swatch}`}>
                          {seller.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="px-1">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-sm font-bold text-black leading-tight line-clamp-1 group-hover:text-gold transition-colors">{seller.name}</h3>
                        {seller.isVerified && <BadgeCheck size={16} className="text-gold shrink-0 mt-0.5" />}
                      </div>
                      {category && (
                        <p className="text-xs text-black/70 line-clamp-1 mb-2">{category}</p>
                      )}
                      <div className="flex items-center justify-end pt-2 border-t border-gray-100">
                        <span className="text-xs font-semibold text-gold inline-flex items-center gap-1">
                          View profile <ArrowRight size={11} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default TopSellers;
